// apps/office/src/pages/office/InteriorProducts.jsx
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, Search, Package, Loader2, Pencil, Trash2, Filter,
  Layers, Tag, Home, Building2, X, CheckSquare, Square,
  CheckCircle, XCircle, ChevronDown,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';
import InteriorProductForm from '../../components/office/InteriorProductForm';
import InteriorGroupsManager from '../../components/office/InteriorGroupsManager';
import InteriorSubcategoriesManager from '../../components/office/InteriorSubcategoriesManager';

const rs = (v) => {
  const n = Number(v) || 0;
  return n === 0 ? '—' : `₹ ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n)}`;
};

const CATEGORY_LABEL = {
  residential: { label: 'Residential', icon: Home, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  commercial: { label: 'Commercial', icon: Building2, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  both: { label: 'Both', icon: Package, color: 'bg-purple-100 text-purple-700 border-purple-200' },
};

const InteriorProducts = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [filterGroup, setFilterGroup] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [formOpen, setFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [subcatsOpen, setSubcatsOpen] = useState(false);

  const isAdmin = ['admin', 'super_admin'].includes(user?.role);

  /* Fetch products */
  const { data: products = [], isLoading, refetch } = useQuery({
    queryKey: ['interior-products'],
    queryFn: () =>
      pb.collection('interior_products').getFullList({
        sort: 'itemId',
        expand: 'groupId',
      }),
    enabled: isAdmin,
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['interior-groups'],
    queryFn: () =>
      pb.collection('interior_product_groups').getFullList({ sort: 'sortOrder,name' }),
    enabled: isAdmin,
  });

  const { data: subcategories = [] } = useQuery({
    queryKey: ['interior-subcategories'],
    queryFn: () =>
      pb.collection('interior_subcategories').getFullList({ sort: 'sortOrder,name' }),
    enabled: isAdmin,
  });

  /* Delete */
  const deleteMutation = useMutation({
    mutationFn: (id) => pb.collection('interior_products').delete(id),
    onSuccess: () => {
      toast.success('Product deleted');
      queryClient.invalidateQueries(['interior-products']);
      refetch();
    },
    onError: (err) => toast.error('Delete failed: ' + err.message),
  });

  /* Bulk delete */
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids) => {
      for (const id of ids) {
        try { await pb.collection('interior_products').delete(id); } catch {}
      }
    },
    onSuccess: () => {
      toast.success(`Deleted ${selectedIds.size} products`);
      setSelectedIds(new Set());
      queryClient.invalidateQueries(['interior-products']);
      refetch();
    },
  });

  /* Filter */
  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return products.filter((p) => {
      if (filterGroup !== 'all' && p.groupId !== filterGroup) return false;
      if (filterCategory !== 'all' && p.categoryType !== filterCategory) return false;
      if (!s) return true;
      return (
        p.name?.toLowerCase().includes(s) ||
        p.itemId?.toLowerCase().includes(s) ||
        p.description?.toLowerCase().includes(s) ||
        p.expand?.groupId?.name?.toLowerCase().includes(s)
      );
    });
  }, [products, search, filterGroup, filterCategory]);

  /* Selection helpers */
  const allSelected = filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id));
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((p) => p.id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const openNew = () => { setEditingProduct(null); setFormOpen(true); };
  const openEdit = (p) => { setEditingProduct(p); setFormOpen(true); };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500 font-medium">Access denied — admin only</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-6 sm:p-8 shadow-xl">
        <div className="relative flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <Package className="w-7 h-7" /> Interior Products
            </h1>
            <p className="text-white/80 text-sm mt-1">
              {products.length} products · {groups.length} groups · {subcategories.length} sub-categories
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setSubcatsOpen(true)} variant="outline" className="bg-white/20 text-white border-white/30 hover:bg-white/30 gap-2">
              <Tag className="w-4 h-4" /> Sub-Categories
            </Button>
            <Button onClick={() => setGroupsOpen(true)} variant="outline" className="bg-white/20 text-white border-white/30 hover:bg-white/30 gap-2">
              <Layers className="w-4 h-4" /> Groups
            </Button>
            <Button onClick={openNew} className="bg-white text-emerald-700 hover:bg-emerald-50 shadow-lg gap-2 font-semibold">
              <Plus className="w-4 h-4" /> Add Product
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto flex-1">
          <div className="relative max-w-md w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, item ID, group…"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <Select value={filterGroup} onValueChange={setFilterGroup}>
            <SelectTrigger className="w-[180px]">
              <Layers className="w-3.5 h-3.5 mr-2 text-slate-400" />
              <SelectValue placeholder="All Groups" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Groups</SelectItem>
              {groups.map((g) => (
                <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[170px]">
              <Filter className="w-3.5 h-3.5 mr-2 text-slate-400" />
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="residential">Residential</SelectItem>
              <SelectItem value="commercial">Commercial</SelectItem>
              <SelectItem value="both">Both</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bulk actions */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">
            <span className="text-xs font-semibold text-emerald-700">{selectedIds.size} selected</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (window.confirm(`Delete ${selectedIds.size} products?`)) {
                  bulkDeleteMutation.mutate(Array.from(selectedIds));
                }
              }}
              className="h-7 px-2 text-red-600 hover:bg-red-100 gap-1 text-xs"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} className="h-7 px-2 text-slate-500">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <Card className="border-0 shadow-md overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center">
              <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">
                {products.length === 0 ? 'No products yet' : 'No products match filters'}
              </p>
              {products.length === 0 && (
                <Button onClick={openNew} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white gap-2">
                  <Plus className="w-4 h-4" /> Add First Product
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="w-10">
                    <button onClick={toggleAll} className="flex items-center justify-center">
                      {allSelected ? (
                        <CheckSquare className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <Square className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="w-20">Image</TableHead>
                  <TableHead className="w-24">Item ID</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Group</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Prices (Standard → Luxury)</TableHead>
                  <TableHead className="text-right w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => {
                  const isSelected = selectedIds.has(p.id);
                  const catInfo = CATEGORY_LABEL[p.categoryType] || CATEGORY_LABEL.both;
                  const CatIcon = catInfo.icon;
                  const activeSubs = subcategories.filter((s) => s.isActive);

                  return (
                    <TableRow key={p.id} className={isSelected ? 'bg-emerald-50/40' : 'hover:bg-slate-50'}>
                      <TableCell>
                        <button onClick={() => toggleOne(p.id)} className="flex items-center justify-center">
                          {isSelected ? (
                            <CheckSquare className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <Square className="w-4 h-4 text-slate-400" />
                          )}
                        </button>
                      </TableCell>

                      <TableCell>
                        {p.image ? (
                          <img
                            src={pb.files.getURL(p, p.image)}
                            alt={p.name}
                            className="w-12 h-12 object-cover rounded-md border border-slate-200"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-slate-100 rounded-md flex items-center justify-center">
                            <Package className="w-5 h-5 text-slate-300" />
                          </div>
                        )}
                      </TableCell>

                      <TableCell className="font-mono text-xs text-emerald-600 font-bold">{p.itemId || '—'}</TableCell>

                      <TableCell>
                        <p className="font-semibold text-slate-800">{p.name}</p>
                        {p.description && (
                          <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">{p.description}</p>
                        )}
                        {p.unit && (
                          <p className="text-[10px] text-slate-400 mt-0.5">per {p.unit}</p>
                        )}
                      </TableCell>

                      <TableCell>
                        <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs">
                          {p.expand?.groupId?.name || '—'}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        <Badge className={`${catInfo.color} text-xs border gap-1`}>
                          <CatIcon className="w-3 h-3" /> {catInfo.label}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        <div className="flex flex-wrap justify-end gap-1 text-[10px]">
                          {activeSubs.map((s) => {
                            const stdPrice = p.prices?.standard?.[s.id];
                            const luxPrice = p.prices?.luxury?.[s.id];
                            if (!stdPrice && !luxPrice) return null;
                            return (
                              <div key={s.id} className="bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-[10px]">
                                <span className="text-slate-500">{s.name}:</span>{' '}
                                <span className="font-semibold text-blue-600">{rs(stdPrice)}</span>
                                <span className="text-slate-300 mx-0.5">/</span>
                                <span className="font-semibold text-amber-600">{rs(luxPrice)}</span>
                              </div>
                            );
                          })}
                        </div>
                      </TableCell>

                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => openEdit(p)}
                            className="h-8 px-2 text-slate-500 hover:text-slate-800"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => {
                              if (window.confirm(`Delete "${p.name}"?`)) deleteMutation.mutate(p.id);
                            }}
                            className="h-8 px-2 text-red-500 hover:text-red-700"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <InteriorProductForm
        open={formOpen}
        onOpenChange={(v) => {
          setFormOpen(v);
          if (!v) setEditingProduct(null);
        }}
        existingProduct={editingProduct}
        onSuccess={() => {
          queryClient.invalidateQueries(['interior-products']);
          refetch();
        }}
      />

      <InteriorGroupsManager open={groupsOpen} onOpenChange={setGroupsOpen} />
      <InteriorSubcategoriesManager open={subcatsOpen} onOpenChange={setSubcatsOpen} />
    </div>
  );
};

export default InteriorProducts;
